import { Directive, EventEmitter, HostListener, Output } from '@angular/core';
import { isEditableEventTarget, isMacPlatform } from '@shared/utils';

@Directive({
  selector: '[tsEditorKeybindings]',
  standalone: false,
})
export class EditorKeybindingsDirective {
  private readonly isMac = isMacPlatform();

  @Output() ctrlS = new EventEmitter<void>();
  @Output() ctrlMetaPressed = new EventEmitter<boolean>();
  @Output() shiftPressed = new EventEmitter<boolean>();
  @Output() spacePressed = new EventEmitter<boolean>();
  @Output() delete = new EventEmitter<void>();
  @Output() esc = new EventEmitter<boolean>();
  @Output() tab = new EventEmitter<void>();
  @Output() shiftTab = new EventEmitter<void>();
  @Output() metaNumber = new EventEmitter<number>();
  @Output() enter = new EventEmitter<void>();

  @HostListener('document:keydown.meta.s', ['$event'])
  @HostListener('document:keydown.control.s', ['$event'])
  public onSaveShortcut(event: KeyboardEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.ctrlS.emit();
  }

  @HostListener('document:keydown.meta')
  public onMetaShortcutPress() {
    if (!this.isMac) return;
    this.ctrlMetaPressed.emit(true);
  }

  @HostListener('document:keydown.control')
  public onControlShortcutPress() {
    if (this.isMac) return;
    this.ctrlMetaPressed.emit(true);
  }

  @HostListener('document:keydown.shift', ['$event'])
  public onDragSelectShortcutPress(event: KeyboardEvent) {
    if (isEditableEventTarget(event)) return;
    this.shiftPressed.emit(true);
  }

  @HostListener('document:keydown.escape')
  public onEscape() {
    this.esc.emit(true);
  }

  @HostListener('document:keydown.shift.tab', ['$event'])
  public onShiftTabPressed(event: KeyboardEvent) {
    if (isEditableEventTarget(event)) return;
    event.preventDefault();
    this.shiftTab.emit();
  }

  @HostListener('document:keydown.tab', ['$event'])
  public onTabPressed(event: KeyboardEvent) {
    if (event.shiftKey) return;
    if (isEditableEventTarget(event)) return;
    event.preventDefault();
    this.tab.emit();
  }

  @HostListener('document:keydown', ['$event'])
  public onMetaNumberPressed(event: KeyboardEvent) {
    if (!(event.metaKey || event.ctrlKey)) return;
    if (event.key < '1' || event.key > '9') return;

    event.preventDefault();
    this.metaNumber.emit(Number(event.key));
  }

  @HostListener('document:keydown.space', ['$event'])
  public onSpacePressed(event: KeyboardEvent) {
    if (isEditableEventTarget(event)) return;
    this.spacePressed.emit(true);
  }

  @HostListener('document:keydown.enter', ['$event'])
  public onEnterPressed(event: KeyboardEvent) {
    if (isEditableEventTarget(event)) return;
    this.enter.emit();
  }

  @HostListener('document:keydown.delete', ['$event'])
  @HostListener('document:keydown.backspace', ['$event'])
  public onDelBackPressed(event: KeyboardEvent) {
    if (isEditableEventTarget(event)) return;
    this.delete.emit();
  }

  @HostListener('document:keydown.control.delete')
  @HostListener('document:keydown.meta.backspace')
  public onModifierDelBackPressed() {
    this.delete.emit();
  }

  @HostListener('document:keyup.meta')
  public onMetaShortcutRelease(): void {
    if (!this.isMac) return;
    this.ctrlMetaPressed.emit(false);
  }

  @HostListener('document:keyup.control')
  public onControlShortcutRelease(): void {
    if (this.isMac) return;
    this.ctrlMetaPressed.emit(false);
  }

  @HostListener('document:keyup.shift', ['$event'])
  public onDragSelectShortcutRelease(event: KeyboardEvent) {
    if (isEditableEventTarget(event)) return;
    this.shiftPressed.emit(false);
  }

  @HostListener('document:keyup.escape')
  public onEscapeRelease() {
    this.esc.emit(false);
  }

  @HostListener('document:keyup.space', ['$event'])
  public onSpaceRelease(event: KeyboardEvent) {
    if (isEditableEventTarget(event)) return;
    this.spacePressed.emit(false);
  }
}
